import React, { useState } from 'react';
import { pdfjs, Document, Page, PDFPageProxy } from 'react-pdf';
import {
  ArrowLeft16,
  ArrowRight16,
  ZoomIn16,
  ZoomOut16
} from '@carbon/icons-react';
import { isIOS } from 'react-device-detect';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
const maxScale = 2;

const PdfViewer: React.FC<Props> = ({ fileUrl }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1);
  const [pageWidth, setPageWidth] = useState<number | undefined>(undefined);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  const onPageLoadSuccess = (page: PDFPageProxy) => {
    if (isIOS) {
      const { originalHeight, originalWidth } = page;
      const maxAreaLimit = 16777216;
      const pixelRatio = window.devicePixelRatio || 1;
      const areaAfterZoom =
        originalHeight *
        pixelRatio *
        maxScale *
        (originalWidth * pixelRatio * maxScale);
      if (areaAfterZoom > maxAreaLimit) {
        const ratio = originalWidth / originalHeight;
        const adjustedWidth =
          Math.sqrt(maxAreaLimit / ratio) / (pixelRatio * maxScale);
        setPageWidth(Number(adjustedWidth.toFixed(2)));
      }
    }
  };

  const goPrevious = () => {
    if (pageNumber !== 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const goNext = () => {
    if (pageNumber !== numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const zoomIn = () => {
    if (scale < maxScale) {
      setScale(scale + 0.1);
    }
  };

  const zoomOut = () => {
    if (scale > 0) {
      setScale(scale - 0.1);
    }
  };

  return (
    <div className="pdf-viewer-container">
      <Document
        className="pdf-viewer-document"
        file={fileUrl}
        onLoadSuccess={onDocumentLoadSuccess}
      >
        <Page
          pageNumber={pageNumber}
          scale={scale}
          width={pageWidth}
          onLoadSuccess={onPageLoadSuccess}
        />
      </Document>
      {numPages > 0 && (
        <div className="pdf-viewer-controls">
          <div className="pdf-viewer-controls-page-label">Page</div>
          <div className="pdf-viewer-controls-btn" onClick={() => goPrevious()}>
            <ArrowLeft16 />
          </div>
          <div>
            {pageNumber} / {numPages}
          </div>
          <div className="pdf-viewer-controls-btn" onClick={() => goNext()}>
            <ArrowRight16 />
          </div>
          <div className="pdf-viewer-controls-btn" onClick={() => zoomOut()}>
            <ZoomOut16 />
          </div>
          <div className="pdf-viewer-controls-btn" onClick={() => zoomIn()}>
            <ZoomIn16 />
          </div>
        </div>
      )}
    </div>
  );
};

type Props = {
  fileUrl: string;
};

export default React.memo(PdfViewer);
